/**
 * 抖店商品编辑（独立于精细化上架 product 模块）
 */
import Call from "@/script/call";

const PRELOAD_RANGE = 2;
const DETAIL_CONCURRENCY = 3;

function productKey(item) {
    if (!item) {
        return "";
    }
    return String(item.id || `${item.shop_id}:${item.product_id}`);
}

/**
 * 会话草稿轻量拷贝：业务字段深拷贝，raw 只读快照共享引用（体积大且保存差分用 _orig_*）
 * 不碰 SKU 笛卡尔逻辑，仅减轻 markDirty / 切品时的卡顿
 */
function cloneDraftForSession(draft) {
    if (!draft || typeof draft !== "object") {
        return draft;
    }
    const rawRef = draft.raw;
    let cloned;
    try {
        const { raw: _omit, ...rest } = draft;
        cloned = JSON.parse(JSON.stringify(rest));
    } catch (e) {
        cloned = { ...draft };
        delete cloned.raw;
    }
    if (rawRef != null) {
        cloned.raw = rawRef;
    }
    return cloned;
}

/** 有限并发执行（用于多商品 ID 搜索等） */
async function mapPool(items, limit, mapper) {
    const list = Array.isArray(items) ? items : [];
    const concurrency = Math.max(1, Math.min(limit || 1, list.length || 1));
    const results = new Array(list.length);
    let cursor = 0;
    async function worker() {
        while (cursor < list.length) {
            const i = cursor++;
            results[i] = await mapper(list[i], i);
        }
    }
    const workers = [];
    for (let w = 0; w < concurrency; w++) {
        workers.push(worker());
    }
    await Promise.all(workers);
    return results;
}

const ID_SEARCH_CONCURRENCY = 4;

const state = {
    tab: "onSale",
    productList: [],
    total: 0,
    page: 1,
    pageSize: 20,
    listLoading: false,
    listLoadingMore: false,
    listError: "",
    /** 上一页是否拉满 pageSize（应对 total 不准/等于本页条数） */
    listMayHaveMore: false,
    listExhausted: false,
    /** 最近一次列表查询条件，供编辑页滚底加载下一页复用 */
    listQuery: {
        id_name_code: "",
    },
    editVisible: false,
    editIndex: -1,
    editDraft: null,
    editLoading: false,
    saving: false,
    /** 正在提交抖店的商品 key，避免切品后台保存与当前编辑互相覆盖 */
    savingKeys: {},
    dirtyMap: {},
    detailCache: {},
    shopIds: [],
};

const mutations = {
    SET_TAB(state, tab) {
        state.tab = tab || "onSale";
    },
    SET_PRODUCT_LIST(state, { list, total, page }) {
        state.productList = Array.isArray(list) ? list : [];
        state.total = total || 0;
        if (page != null) {
            state.page = page;
        }
    },
    APPEND_PRODUCT_LIST(state, { list, total, page }) {
        const map = new Map();
        (state.productList || []).forEach((it) => {
            const k = productKey(it);
            if (k) {
                map.set(k, it);
            }
        });
        (list || []).forEach((it) => {
            const k = productKey(it);
            if (k && !map.has(k)) {
                map.set(k, it);
            }
        });
        state.productList = Array.from(map.values());
        if (total != null) {
            state.total = total;
        }
        if (page != null) {
            state.page = page;
        }
    },
    SET_PAGE(state, page) {
        state.page = page;
    },
    SET_PAGE_SIZE(state, size) {
        state.pageSize = size;
    },
    SET_LIST_LOADING(state, v) {
        state.listLoading = !!v;
    },
    SET_LIST_LOADING_MORE(state, v) {
        state.listLoadingMore = !!v;
    },
    SET_LIST_ERROR(state, msg) {
        state.listError = msg || "";
    },
    SET_LIST_QUERY(state, query) {
        state.listQuery = {
            ...(state.listQuery || {}),
            ...(query && typeof query === "object" ? query : {}),
        };
    },
    SET_LIST_MAY_HAVE_MORE(state, v) {
        state.listMayHaveMore = !!v;
    },
    SET_LIST_EXHAUSTED(state, v) {
        state.listExhausted = !!v;
    },
    SET_SHOP_IDS(state, ids) {
        state.shopIds = Array.isArray(ids) ? ids : [];
    },
    SET_EDIT_VISIBLE(state, v) {
        state.editVisible = !!v;
    },
    SET_EDIT_INDEX(state, index) {
        state.editIndex = index;
    },
    SET_EDIT_DRAFT(state, draft) {
        state.editDraft = draft;
    },
    SET_EDIT_LOADING(state, v) {
        state.editLoading = !!v;
    },
    SET_SAVING(state, v) {
        state.saving = !!v;
    },
    SET_SAVING_KEY(state, { key, saving }) {
        if (!key) {
            return;
        }
        const next = { ...state.savingKeys };
        if (saving) {
            next[key] = true;
        } else {
            delete next[key];
        }
        state.savingKeys = next;
    },
    PATCH_EDIT_DRAFT(state, patch) {
        if (!state.editDraft || !patch || typeof patch !== "object") {
            return;
        }
        state.editDraft = { ...state.editDraft, ...patch };
    },
    SET_DIRTY(state, { key, draft }) {
        if (!key) {
            return;
        }
        state.dirtyMap = { ...state.dirtyMap, [key]: draft };
    },
    CLEAR_DIRTY(state, key) {
        if (!key || !state.dirtyMap[key]) {
            return;
        }
        const next = { ...state.dirtyMap };
        delete next[key];
        state.dirtyMap = next;
    },
    SET_DETAIL_CACHE(state, { key, draft }) {
        if (!key || !draft) {
            return;
        }
        state.detailCache = { ...state.detailCache, [key]: draft };
    },
    UPDATE_LIST_ITEM(state, { key, patch }) {
        const idx = state.productList.findIndex((it) => productKey(it) === key);
        if (idx < 0) {
            return;
        }
        const row = { ...state.productList[idx], ...patch };
        const list = state.productList.slice();
        list.splice(idx, 1, row);
        state.productList = list;
    },
    /** 批量上架/下架/删除成功后从当前列表移除，并修正正在编辑的下标 */
    REMOVE_PRODUCT_KEYS(state, keys) {
        const keySet = new Set(keys || []);
        if (!keySet.size) {
            return;
        }
        const cur = state.productList[state.editIndex];
        const curKey = cur ? productKey(cur) : "";
        const next = state.productList.filter((it) => !keySet.has(productKey(it)));
        const removed = state.productList.length - next.length;
        state.productList = next;
        state.total = Math.max(0, (parseInt(state.total, 10) || 0) - removed);
        const dirty = { ...state.dirtyMap };
        const cache = { ...state.detailCache };
        keySet.forEach((key) => {
            delete dirty[key];
            delete cache[key];
        });
        state.dirtyMap = dirty;
        state.detailCache = cache;
        if (!curKey || !state.editVisible) {
            return;
        }
        if (keySet.has(curKey)) {
            state.editDraft = null;
            if (!next.length) {
                state.editIndex = -1;
            } else {
                const idx = state.editIndex < 0 ? 0 : state.editIndex;
                state.editIndex = Math.min(idx, next.length - 1);
            }
            return;
        }
        const idx = next.findIndex((it) => productKey(it) === curKey);
        if (idx >= 0) {
            state.editIndex = idx;
        }
    },
    RESET_EDIT_SESSION(state) {
        state.editVisible = false;
        state.editIndex = -1;
        state.editDraft = null;
        state.editLoading = false;
        state.saving = false;
        state.savingKeys = {};
    },
};

const getters = {
    currentListItem(state) {
        if (state.editIndex < 0 || state.editIndex >= state.productList.length) {
            return null;
        }
        return state.productList[state.editIndex];
    },
    isCurrentDirty(state, getters) {
        const item = getters.currentListItem;
        if (!item) {
            return false;
        }
        return !!state.dirtyMap[productKey(item)];
    },
    hasMoreList(state) {
        if (state.listExhausted) {
            return false;
        }
        const total = Number(state.total) || 0;
        const loaded = (state.productList || []).length;
        const pageSize = Math.max(1, Number(state.pageSize) || 20);
        if (total > 0 && loaded < total) {
            return true;
        }
        if (state.listMayHaveMore) {
            return true;
        }
        // 修复前已加载的会话：满页且 total 恰好等于已加载数时，允许再试一页
        if (loaded > 0 && loaded % pageSize === 0 && (total <= 0 || total === loaded)) {
            return true;
        }
        return false;
    },
};

const actions = {
    async ensureShopList({ dispatch, rootState }) {
        if (!rootState.product?.shopList?.length) {
            await dispatch("product/getShopList", true, { root: true });
        }
        return rootState.product.shopList || [];
    },

    resolveShops({ rootState }, shopIds) {
        const all = rootState.product?.shopList || [];
        const ids = (shopIds || []).map(String);
        return all.filter((s) => s && ids.includes(String(s.id)) && s.source_type == 1);
    },

    async fetchList({ commit, dispatch, state }, payload = {}) {
        const append = !!payload.append;
        if (append) {
            commit("SET_LIST_LOADING_MORE", true);
        } else {
            commit("SET_LIST_LOADING", true);
            commit("SET_LIST_ERROR", "");
        }
        try {
            await dispatch("ensureShopList");
            const shopIds = payload.shopIds != null ? payload.shopIds : state.shopIds;
            const shops = await dispatch("resolveShops", shopIds);
            if (!shops.length) {
                if (!append) {
                    commit("SET_PRODUCT_LIST", { list: [], total: 0, page: 1 });
                    commit("SET_LIST_ERROR", "请选择已登录的抖店店铺");
                    commit("SET_LIST_MAY_HAVE_MORE", false);
                    commit("SET_LIST_EXHAUSTED", true);
                }
                return { code: -1, msg: "请选择店铺" };
            }
            const tab = payload.tab != null ? payload.tab : state.tab;
            const page = payload.page != null ? payload.page : state.page;
            const pageSize = payload.pageSize != null ? payload.pageSize : state.pageSize;
            const idNameCode =
                payload.id_name_code != null
                    ? payload.id_name_code
                    : state.listQuery?.id_name_code || "";
            if (!append) {
                commit("SET_TAB", tab);
                commit("SET_SHOP_IDS", shopIds);
                commit("SET_LIST_QUERY", { id_name_code: idNameCode });
                commit("SET_LIST_EXHAUSTED", false);
            }

            const opts = {
                tab,
                page,
                size: pageSize,
                shopConcurrency: payload.shopConcurrency || 2,
                product_id: payload.product_id || "",
                name: payload.name || "",
                start_time: payload.start_time,
                end_time: payload.end_time,
                price_lower: payload.price_lower,
                price_higher: payload.price_higher,
                sell_num_lower: payload.sell_num_lower,
                sell_num_higher: payload.sell_num_higher,
            };

            // 多 ID：按 product_id 逐个/批量；简单场景传 name 或单个 product_id
            const idName = String(idNameCode || "").trim();
            let rs;
            if (idName) {
                const tokens = idName
                    .split(/[\s,，\n]+/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                    .slice(0, 100);
                const looksLikeId = tokens.every((t) => /^\d{5,}$/.test(t));
                if (looksLikeId && tokens.length === 1) {
                    opts.product_id = tokens[0];
                    rs = await Call.invoke("doudianShopEditApi.listProductsMultiShop", shops, opts);
                } else if (looksLikeId && tokens.length > 1) {
                    // 多 ID 一次拉齐，无下一页
                    if (append) {
                        commit("SET_LIST_MAY_HAVE_MORE", false);
                        commit("SET_LIST_EXHAUSTED", true);
                        return { code: 1, data: { list: [], total: state.total }, msg: "no-more" };
                    }
                    const parts = await mapPool(tokens, ID_SEARCH_CONCURRENCY, async (pid) => {
                        const one = await Call.invoke(
                            "doudianShopEditApi.listProductsMultiShop",
                            shops,
                            { ...opts, product_id: pid, page: 1 },
                        );
                        return one?.code === 1 ? one.data?.list || [] : [];
                    });
                    const allLists = [];
                    parts.forEach((list) => {
                        if (Array.isArray(list) && list.length) {
                            allLists.push(...list);
                        }
                    });
                    const uniq = new Map();
                    allLists.forEach((it) => uniq.set(productKey(it), it));
                    rs = {
                        code: 1,
                        data: {
                            list: Array.from(uniq.values()),
                            total: uniq.size,
                            page_full: false,
                        },
                    };
                } else {
                    opts.name = tokens[0] || idName;
                    rs = await Call.invoke("doudianShopEditApi.listProductsMultiShop", shops, opts);
                }
            } else {
                rs = await Call.invoke("doudianShopEditApi.listProductsMultiShop", shops, opts);
            }

            if (rs?.code !== 1) {
                if (!append) {
                    commit("SET_PRODUCT_LIST", { list: [], total: 0, page });
                    commit("SET_LIST_ERROR", rs?.msg || "加载失败");
                    commit("SET_LIST_MAY_HAVE_MORE", false);
                    commit("SET_LIST_EXHAUSTED", true);
                }
                return rs;
            }

            const gotList = rs.data.list || [];
            const got = gotList.length;
            const beforeLen = (state.productList || []).length;
            if (append) {
                commit("APPEND_PRODUCT_LIST", {
                    list: gotList,
                    total: rs.data.total || state.total,
                    page,
                });
            } else {
                commit("SET_PRODUCT_LIST", {
                    list: gotList,
                    total: rs.data.total || 0,
                    page,
                });
            }
            const afterLen = (state.productList || []).length;
            const pageFull =
                rs.data.page_full === true || got >= pageSize;
            const grew = afterLen > beforeLen;
            if (append && (!got || !grew)) {
                commit("SET_LIST_MAY_HAVE_MORE", false);
                commit("SET_LIST_EXHAUSTED", true);
            } else {
                commit("SET_LIST_MAY_HAVE_MORE", pageFull);
                if (!pageFull) {
                    commit("SET_LIST_EXHAUSTED", true);
                } else if (!append) {
                    commit("SET_LIST_EXHAUSTED", false);
                }
            }
            return rs;
        } catch (e) {
            if (!append) {
                commit("SET_LIST_ERROR", e?.message || String(e));
            }
            return { code: -1, msg: e?.message || String(e) };
        } finally {
            if (append) {
                commit("SET_LIST_LOADING_MORE", false);
            } else {
                commit("SET_LIST_LOADING", false);
            }
        }
    },

    /** 编辑页左侧滚底：加载下一页并追加到当前列表 */
    async loadMoreList({ dispatch, state, getters }) {
        if (state.listLoading || state.listLoadingMore) {
            return { code: 1, msg: "busy" };
        }
        if (!getters.hasMoreList) {
            return { code: 1, msg: "no-more" };
        }
        const nextPage = (parseInt(state.page, 10) || 1) + 1;
        return await dispatch("fetchList", {
            append: true,
            page: nextPage,
            pageSize: state.pageSize,
            shopIds: state.shopIds,
            tab: state.tab,
            id_name_code: state.listQuery?.id_name_code || "",
        });
    },

    async openEdit({ commit, dispatch, state }, index) {
        const idx = parseInt(index, 10);
        if (Number.isNaN(idx) || idx < 0 || idx >= state.productList.length) {
            return { code: -1, msg: "索引无效" };
        }
        commit("SET_EDIT_VISIBLE", true);
        commit("SET_EDIT_INDEX", idx);
        await dispatch("loadCurrentDetail");
        dispatch("preloadNeighbors");
        return { code: 1 };
    },

    async loadCurrentDetail({ commit, state, getters }) {
        const item = getters.currentListItem;
        if (!item?.shop || !item.product_id) {
            commit("SET_EDIT_DRAFT", null);
            return;
        }
        const key = productKey(item);
        if (state.dirtyMap[key]) {
            commit("SET_EDIT_DRAFT", cloneDraftForSession(state.dirtyMap[key]));
            return;
        }
        if (state.detailCache[key]) {
            const cached = state.detailCache[key];
            const fmt = cached?.product_format_new;
            const hasFmt =
                fmt &&
                typeof fmt === "object" &&
                !Array.isArray(fmt) &&
                Object.keys(fmt).length > 0;
            const hasBrand =
                Array.isArray(fmt?.["1687"]) && fmt["1687"].length > 0;
            const main34 = cached?.main_images_3_4 || [];
            const main34Broken =
                main34.length > 0 &&
                !main34.some((it) => {
                    const u = String(it?.url || it || "").trim();
                    return /^https?:\/\//i.test(u) || u.startsWith("//");
                });
            // 旧缓存未跑规格图映射/视频封面补全 → 强制重拉
            const needRemap = Number(cached?._norm_ver || 0) < 5;
            // 半成品预加载（仅 detail、无 attr_json/update_rule）不可直接进编辑
            const incompleteBundle = Number(cached?._bundle_ver || 0) < 1;
            if (hasFmt && hasBrand && !main34Broken && !needRemap && !incompleteBundle) {
                commit("SET_EDIT_DRAFT", cloneDraftForSession(cached));
                return;
            }
        }
        commit("SET_EDIT_LOADING", true);
        try {
            const showDraft = state.tab === "draft" || item.tab === "draft";
            const rs = await Call.invoke(
                "doudianShopEditApi.loadEditBundle",
                item.shop,
                item.product_id,
                { show_draft: showDraft, listMeta: item },
            );
            if (rs?.code === 1 && rs.data) {
                commit("SET_EDIT_DRAFT", rs.data);
                commit("SET_DETAIL_CACHE", { key, draft: rs.data });
            } else {
                commit("SET_EDIT_DRAFT", null);
                window.vue?.$message?.error(rs?.msg || "加载商品详情失败");
            }
        } catch (e) {
            commit("SET_EDIT_DRAFT", null);
            window.vue?.$message?.error(e?.message || "加载失败");
        } finally {
            commit("SET_EDIT_LOADING", false);
        }
    },

    async refreshDetailQuiet({ commit, state }, item) {
        if (!item?.shop || !item.product_id) {
            return;
        }
        const key = productKey(item);
        if (state.dirtyMap[key]) {
            return;
        }
        try {
            const showDraft = state.tab === "draft" || item.tab === "draft";
            const rs = await Call.invoke(
                "doudianShopEditApi.loadEditBundle",
                item.shop,
                item.product_id,
                { show_draft: showDraft, listMeta: item },
            );
            if (rs?.code === 1 && rs.data) {
                commit("SET_DETAIL_CACHE", { key, draft: rs.data });
                if (productKey(state.productList[state.editIndex]) === key && !state.dirtyMap[key]) {
                    commit("SET_EDIT_DRAFT", rs.data);
                }
            }
        } catch (e) {
            /* ignore quiet refresh */
        }
    },

    async preloadNeighbors({ state, commit }) {
        const idx = state.editIndex;
        const list = state.productList;
        if (idx < 0 || !list.length) {
            return;
        }
        const targets = [];
        for (let d = 1; d <= PRELOAD_RANGE; d++) {
            if (idx + d < list.length) {
                targets.push(list[idx + d]);
            }
            if (idx - d >= 0) {
                targets.push(list[idx - d]);
            }
        }
        const need = targets.filter((it) => {
            const key = productKey(it);
            return it && !state.detailCache[key] && !state.dirtyMap[key];
        });
        // 按店铺分组预加载
        const byShop = new Map();
        need.forEach((it) => {
            const sid = String(it.shop_id || it.shop?.id);
            if (!byShop.has(sid)) {
                byShop.set(sid, { shop: it.shop, ids: [], items: [] });
            }
            byShop.get(sid).ids.push(it.product_id);
            byShop.get(sid).items.push(it);
        });
        const shopJobs = Array.from(byShop.values());
        await Promise.all(
            shopJobs.map(async (group) => {
                try {
                    const showDraft = state.tab === "draft";
                    const rs = await Call.invoke(
                        "doudianShopEditApi.preloadDetails",
                        group.shop,
                        group.ids,
                        { concurrency: DETAIL_CONCURRENCY, show_draft: showDraft },
                    );
                    (rs?.data || []).forEach((one) => {
                        if (one?.code === 1 && one.data) {
                            const meta = group.items.find(
                                (it) => String(it.product_id) === String(one.product_id),
                            );
                            const key = productKey(meta || one.data);
                            commit("SET_DETAIL_CACHE", { key, draft: one.data });
                            // 预加载完成时若正看该商品且无脏草稿，直接用完整包
                            if (
                                productKey(state.productList[state.editIndex]) === key &&
                                !state.dirtyMap[key]
                            ) {
                                commit("SET_EDIT_DRAFT", one.data);
                            }
                        }
                    });
                } catch (e) {
                    /* ignore preload errors */
                }
            }),
        );
    },

    markDirty({ commit, state, getters }) {
        const item = getters.currentListItem;
        if (!item || !state.editDraft) {
            return;
        }
        const key = productKey(item);
        commit("SET_DIRTY", {
            key,
            draft: cloneDraftForSession(state.editDraft),
        });
        commit("UPDATE_LIST_ITEM", { key, patch: { save_status: 0 } });
    },

    patchDraft({ commit, dispatch }, patch) {
        commit("PATCH_EDIT_DRAFT", patch);
        dispatch("markDirty");
    },

    async switchProduct({ dispatch, state, getters, commit }, nextIndex) {
        const idx = parseInt(nextIndex, 10);
        if (Number.isNaN(idx) || idx < 0 || idx >= state.productList.length) {
            return { code: -1 };
        }
        if (idx === state.editIndex) {
            return { code: 1 };
        }
        const cur = getters.currentListItem;
        const curKey = productKey(cur);
        const dirty =
            cur &&
            state.editDraft &&
            (state.dirtyMap[curKey] || getters.isCurrentDirty);
        let pendingSave = null;
        if (dirty) {
            const snapshot = cloneDraftForSession(state.editDraft);
            commit("SET_DIRTY", { key: curKey, draft: snapshot });
            pendingSave = { item: cur, draft: snapshot };
        }
        commit("SET_EDIT_INDEX", idx);
        await dispatch("loadCurrentDetail");
        dispatch("preloadNeighbors");
        // 与精细化上架一致：先切品，后台保存上一件到抖店
        if (pendingSave) {
            dispatch("saveToDouDian", { ...pendingSave, background: true });
        }
        return { code: 1 };
    },

    async saveToDouDian({ commit, state, getters, dispatch }, payload = {}) {
        const item = payload.item || getters.currentListItem;
        const draft = payload.draft || state.editDraft;
        const background = !!payload.background;
        const quiet = !!payload.quiet;
        if (!item?.shop || !draft) {
            return { code: -1, msg: "无编辑数据" };
        }
        const key = productKey(item);
        if (state.savingKeys[key]) {
            return { code: -1, msg: "正在保存" };
        }
        if (!background) {
            commit("SET_SAVING", true);
        }
        commit("SET_SAVING_KEY", { key, saving: true });
        commit("UPDATE_LIST_ITEM", { key, patch: { save_status: 1, save_err: "" } });
        const isStillCurrent = () =>
            productKey(state.productList[state.editIndex]) === key;
        try {
            const rs = await Call.invoke("doudianShopEditApi.saveProduct", item.shop, {
                ...draft,
                // 列表侧状态兜底，用于 commit（草稿/在售/下架）
                tab: draft.tab != null ? draft.tab : item.tab,
                draft_status:
                    draft.draft_status != null ? draft.draft_status : item.draft_status,
                status: draft.status != null ? draft.status : item.status,
                check_status:
                    draft.check_status != null ? draft.check_status : item.check_status,
                // 未点选过时保持原状态
                target_sale_status: draft.target_sale_status || undefined,
            });
            const row = state.productList.find((it) => productKey(it) === key);
            // 保存过程中用户又改了该商品（markDirty 会把 save_status 置 0），不能清草稿、不能覆盖当前编辑
            const editedMore = row?.save_status === 0;
            if (rs?.code === 1 || rs?.code === 10000) {
                const savedDraft = rs.draft || draft;
                if (!editedMore) {
                    commit("CLEAR_DIRTY", key);
                    commit("SET_DETAIL_CACHE", {
                        key,
                        draft: cloneDraftForSession(savedDraft),
                    });
                    if (rs.draft && isStillCurrent()) {
                        commit("SET_EDIT_DRAFT", rs.draft);
                    }
                    commit("UPDATE_LIST_ITEM", {
                        key,
                        patch: {
                            save_status: 2,
                            title: savedDraft.title,
                            pic: savedDraft.images?.[0]?.url || item.pic,
                            status: savedDraft.status,
                            draft_status: savedDraft.draft_status,
                            tab: savedDraft.tab || item.tab,
                            check_status:
                                savedDraft.check_status != null
                                    ? savedDraft.check_status
                                    : item.check_status,
                        },
                    });
                }
                if (!quiet) {
                    window.vue?.$message?.success(rs.msg || "已保存到抖店");
                }
                return { code: 1, draft: savedDraft, msg: rs.msg || "已保存到抖店" };
            }
            const msg = rs?.msg || "保存失败";
            const failDraft = rs?.draft || draft;
            commit("SET_DIRTY", {
                key,
                draft: cloneDraftForSession(failDraft),
            });
            if (rs?.draft && isStillCurrent() && !editedMore) {
                commit("SET_EDIT_DRAFT", rs.draft);
                commit("SET_DETAIL_CACHE", { key, draft: cloneDraftForSession(rs.draft) });
            }
            commit("UPDATE_LIST_ITEM", { key, patch: { save_status: 3, save_err: msg } });
            if (!quiet) {
                window.vue?.$message?.error(msg);
            }
            return { code: -1, msg, draft: failDraft };
        } catch (e) {
            const msg = e?.message || String(e);
            commit("SET_DIRTY", { key, draft: cloneDraftForSession(draft) });
            commit("UPDATE_LIST_ITEM", { key, patch: { save_status: 3, save_err: msg } });
            if (!quiet) {
                window.vue?.$message?.error(msg);
            }
            return { code: -1, msg };
        } finally {
            commit("SET_SAVING_KEY", { key, saving: false });
            if (!background) {
                commit("SET_SAVING", false);
            }
            // 切走后又改回来再切走：上一轮结束后补一次保存
            const row = state.productList.find((it) => productKey(it) === key);
            if (background && state.dirtyMap[key] && row?.save_status === 0) {
                dispatch("saveToDouDian", {
                    item: row,
                    draft: state.dirtyMap[key],
                    background: true,
                    quiet,
                });
            }
        }
    },

    closeEdit({ commit }) {
        commit("RESET_EDIT_SESSION");
    },

    /** 编辑页左侧勾选：批量上架 / 下架 / 删除 */
    async batchShelfOps({ commit, dispatch, state, getters }, payload = {}) {
        const op = payload.op;
        const items = Array.isArray(payload.items) ? payload.items : [];
        if (!items.length) {
            return { code: -1, msg: "请先勾选商品", data: { success: 0, fail: 0, fails: [] } };
        }
        const shopMap = new Map();
        const compact = [];
        items.forEach((it) => {
            const shop = it?.shop;
            const shopId = shop?.id || it?.shop_id;
            const productId = String(it?.product_id || "").trim();
            if (!shop || shopId == null || shopId === "" || !productId) {
                return;
            }
            if (!shopMap.has(String(shopId))) {
                shopMap.set(String(shopId), shop);
            }
            compact.push({ product_id: productId, shop_id: shopId });
        });
        if (!compact.length) {
            return { code: -1, msg: "勾选商品缺少店铺或商品ID", data: { success: 0, fail: 0, fails: [] } };
        }
        const currentKey = productKey(getters.currentListItem);
        let rs;
        try {
            rs = await Call.invoke(
                "doudianShopEditApi.batchShelfOps",
                Array.from(shopMap.values()),
                compact,
                { op, tab: state.tab, concurrency: payload.concurrency },
            );
        } catch (e) {
            return { code: -1, msg: e?.message || "操作失败", data: { success: 0, fail: 0, fails: [] } };
        }
        const rows = Array.isArray(rs?.data?.results) ? rs.data.results : [];
        if (!rows.length && rs?.code !== 1) {
            return {
                code: -1,
                msg: rs?.msg || "操作失败",
                data: { success: 0, fail: compact.length, fails: [] },
            };
        }
        const successKeys = [];
        const fails = [];
        rows.forEach((row) => {
            const key = String(row.shop_id) + ":" + String(row.product_id);
            if (row.code === 1) {
                successKeys.push(key);
                return;
            }
            const msg = row.msg || "失败";
            fails.push({ key, product_id: row.product_id, msg });
            commit("UPDATE_LIST_ITEM", { key, patch: { shelf_err: msg } });
        });
        const removedCurrent = !!(currentKey && successKeys.includes(currentKey));
        if (successKeys.length) {
            commit("REMOVE_PRODUCT_KEYS", successKeys);
        }
        if (removedCurrent && state.editVisible && state.editIndex >= 0) {
            await dispatch("loadCurrentDetail");
        }
        const ok = successKeys.length;
        const bad = fails.length;
        return {
            code: bad ? -1 : 1,
            msg: rs?.msg || (bad ? `成功 ${ok}，失败 ${bad}` : "操作成功"),
            data: { success: ok, fail: bad, fails, successKeys },
        };
    },
};

export default {
    namespaced: true,
    state,
    mutations,
    getters,
    actions,
};
